import { useState, useEffect, useCallback, useRef } from 'react';
import {
    TrendingUp, Trophy, AlertTriangle, Clock, MapPin,
    Loader2, RefreshCw, Wallet, History, Search,
    Target, Zap, X, Minus, Plus, Filter,
    Bookmark, Gift, Info, Radio, DollarSign,
    ArrowUpRight, ArrowDownRight, Activity, Circle,
    Check, CheckCircle2, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetFooter,
} from '../components/ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { useAuth } from '../context/AuthContext';
import { getToken } from '../lib/auth';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Market categories
const MARKET_CATEGORIES = [
    { id: 'all', name: 'All' },
    { id: 'live', name: '🔴 Live' },
    { id: 'upcoming', name: 'Upcoming' },
    { id: 'odds_evens', name: 'Odd/Even' },
    { id: 'red_black', name: 'Red/Black' },
    { id: 'fav_vs_field', name: 'Favourite' },
];

// Convert decimal odds to probability price ($0.00 - $1.00)
const oddsToPrice = (odds) => {
    if (!odds || odds <= 1) return 0.50;
    const impliedProb = 1 / odds;
    return Math.round(impliedProb * 100) / 100;
};

// Format price as currency
const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
};

// Get market status
const getMarketStatus = (raceTime) => {
    if (!raceTime) return 'upcoming';
    const now = new Date();
    const race = new Date(raceTime);
    const diffMs = race - now;
    const diffMins = diffMs / (1000 * 60);

    if (diffMins < -30) return 'settled'; // Race finished (30+ mins ago)
    if (diffMins < 0) return 'live'; // Race is running
    if (diffMins < 5) return 'starting'; // Starting soon
    return 'upcoming';
};

// Get time until race
const getTimeUntil = (raceTime) => {
    if (!raceTime) return '';
    const now = new Date();
    const race = new Date(raceTime);
    const diffMs = race - now;

    if (diffMs < 0) return 'LIVE';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const secs = Math.floor((diffMs % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
};

// Polymarket-style Market Card
const MarketCard = ({ market, onTrade, userPositions, priceChanges = {} }) => {
    const status = market.status === 'settled' ? 'settled' :
                   market.isLive ? 'live' :
                   getMarketStatus(market.race_time);
    const timeUntil = getTimeUntil(market.race_time);
    const userPos = userPositions?.[market.id];
    const isSuspended = status === 'live' || status === 'settled';
    const winningOption = market.result; // The winning option ID

    return (
        <Card className={`group transition-all bg-card overflow-hidden ${
            status === 'settled' ? 'opacity-90' : 'hover:border-primary/50'
        }`}>
            <CardContent className="p-0">
                {/* Status bar */}
                <div className={`px-4 py-2 flex items-center justify-between text-xs ${
                    status === 'settled' ? 'bg-blue-500/10 text-blue-600' :
                    status === 'live' ? 'bg-red-500/10 text-red-500' :
                    status === 'starting' ? 'bg-amber-500/10 text-amber-500' :
                    'bg-muted/50 text-muted-foreground'
                }`}>
                    <div className="flex items-center gap-2">
                        {status === 'settled' && (
                            <>
                                <CheckCircle2 className="h-3 w-3" />
                                <span className="font-medium">SETTLED</span>
                            </>
                        )}
                        {status === 'live' && (
                            <>
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                                <span className="font-medium">RACE IN PROGRESS</span>
                                <span className="text-[10px] opacity-75">• Betting Suspended</span>
                            </>
                        )}
                        {status === 'starting' && (
                            <>
                                <Activity className="h-3 w-3" />
                                <span className="font-medium">Starting in {timeUntil}</span>
                            </>
                        )}
                        {status === 'upcoming' && (
                            <>
                                <Clock className="h-3 w-3" />
                                <span>{timeUntil}</span>
                            </>
                        )}
                    </div>
                    <span className="font-data">
                        {market.totalVolume > 0
                            ? `${market.totalVolume.toLocaleString()} pts wagered`
                            : 'Be first to predict'
                        }
                    </span>
                </div>

                {/* Main content */}
                <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-4">
                        <span className="text-2xl">{market.icon || '🎯'}</span>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                                <h3 className="font-semibold text-sm leading-tight">
                                    {market.title}
                                </h3>
                                <TooltipProvider>
                                    <Tooltip delayDuration={200}>
                                        <TooltipTrigger asChild>
                                            <button className="text-muted-foreground hover:text-primary">
                                                <Info className="h-3.5 w-3.5" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs text-xs p-3">
                                            {market.description}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {market.subtitle}
                            </p>
                        </div>
                    </div>

                    {/* Result banner for settled markets */}
                    {status === 'settled' && winningOption && (
                        <div className="mb-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                            <div className="flex items-center gap-2">
                                <Trophy className="h-4 w-4 text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-700">
                                    Result: <span className="font-bold">{market.resultName || winningOption.toUpperCase()}</span> wins!
                                </span>
                            </div>
                            {market.resultDetails && (
                                <p className="text-xs text-emerald-600/80 mt-1">{market.resultDetails}</p>
                            )}
                        </div>
                    )}

                    {/* Price display - Polymarket style */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        {market.options.map((option, idx) => {
                            const isWinner = winningOption === option.id;
                            const isLoser = winningOption && winningOption !== option.id;
                            const priceChangeKey = `${market.id}_${option.id}`;
                            const priceDirection = priceChanges[priceChangeKey];
                            const hasChange = option.priceChange && Math.abs(option.priceChange) > 0.001;

                            const btn = (
                                <button
                                    key={option.id}
                                    onClick={() => !isSuspended && onTrade(market, option)}
                                    disabled={isSuspended}
                                    className={`w-full p-3 rounded-lg border-2 transition-all relative ${
                                        isSuspended ? 'cursor-not-allowed' : 'hover:scale-[1.02]'
                                    } ${
                                        priceDirection === 'up' ? 'animate-pulse bg-emerald-500/10' :
                                        priceDirection === 'down' ? 'animate-pulse bg-rose-500/10' : ''
                                    } ${
                                        isWinner
                                            ? 'border-emerald-500 bg-emerald-500/10'
                                            : isLoser
                                                ? 'border-gray-300 bg-gray-100/50 opacity-60'
                                                : idx === 0
                                                    ? 'border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/5'
                                                    : 'border-rose-500/30 hover:border-rose-500 hover:bg-rose-500/5'
                                    }`}
                                >
                                    {/* Winner badge */}
                                    {isWinner && (
                                        <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-1">
                                            <Check className="h-3 w-3" />
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`text-xs font-medium ${
                                            isWinner ? 'text-emerald-700' :
                                            isLoser ? 'text-gray-500' :
                                            idx === 0 ? 'text-emerald-600' : 'text-rose-600'
                                        }`}>
                                            {option.name}
                                        </span>
                                        {/* Price change indicator */}
                                        {hasChange && !isSuspended && (
                                            <span className={`text-[10px] flex items-center gap-0.5 transition-opacity ${
                                                option.priceChange > 0 ? 'text-emerald-500' : 'text-rose-500'
                                            }`}>
                                                {option.priceChange > 0 ? (
                                                    <ArrowUpRight className="h-3 w-3" />
                                                ) : (
                                                    <ArrowDownRight className="h-3 w-3" />
                                                )}
                                                <span className="font-data">
                                                    {Math.abs(option.priceChange * 100).toFixed(0)}¢
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-2xl font-bold font-data ${
                                        isWinner ? 'text-emerald-700' :
                                        isLoser ? 'text-gray-400' :
                                        idx === 0 ? 'text-emerald-600' : 'text-rose-600'
                                    }`}>
                                        {status === 'settled' ? (isWinner ? '$1.00' : '$0.00') : formatPrice(option.price)}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        {status === 'settled'
                                            ? (isWinner ? 'Paid out' : 'Expired worthless')
                                            : `${Math.round(option.price * 100)}% chance`
                                        }
                                    </div>
                                </button>
                            );

                            // Wrap in tooltip if we have runner names to show
                            if (option.fullRunners) {
                                return (
                                    <TooltipProvider key={option.id}>
                                        <Tooltip delayDuration={300}>
                                            <TooltipTrigger asChild>{btn}</TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs text-xs p-2">
                                                <p className="font-semibold mb-1">{option.name}</p>
                                                <p className="text-muted-foreground">{option.fullRunners}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                );
                            }
                            return <div key={option.id}>{btn}</div>;
                        })}
                    </div>

                    {/* Suspended overlay message */}
                    {status === 'live' && (
                        <div className="text-center py-2 px-3 rounded bg-red-500/5 border border-red-500/20 mb-3">
                            <span className="text-xs text-red-600 font-medium">
                                🔒 Betting suspended while race is in progress
                            </span>
                        </div>
                    )}

                    {/* Probability bar */}
                    {status !== 'settled' && (
                        <div className="relative h-2 rounded-full overflow-hidden bg-rose-500/20">
                            <div
                                className="absolute left-0 top-0 h-full bg-emerald-500 transition-all duration-300"
                                style={{ width: `${(market.options[0]?.price || 0.5) * 100}%` }}
                            />
                        </div>
                    )}

                    {/* User position */}
                    {userPos && (
                        <div className={`mt-3 p-2 rounded border ${
                            status === 'settled'
                                ? userPos.side.toLowerCase() === winningOption
                                    ? 'bg-emerald-500/10 border-emerald-500/30'
                                    : 'bg-rose-500/10 border-rose-500/30'
                                : 'bg-primary/5 border-primary/20'
                        }`}>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Your position</span>
                                <span className="font-medium">
                                    {userPos.shares} {userPos.side} shares @ {formatPrice(userPos.avgPrice)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-xs mt-1">
                                <span className="text-muted-foreground">
                                    {status === 'settled' ? 'Payout' : 'Potential payout'}
                                </span>
                                <span className={`font-medium ${
                                    status === 'settled'
                                        ? userPos.side.toLowerCase() === winningOption
                                            ? 'text-emerald-600'
                                            : 'text-rose-600'
                                        : 'text-emerald-600'
                                }`}>
                                    {status === 'settled'
                                        ? userPos.side.toLowerCase() === winningOption
                                            ? `+$${userPos.shares.toFixed(2)}`
                                            : '$0.00'
                                        : `$${(userPos.shares * 1).toFixed(2)}`
                                    }
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// Trade dialog
const TradeDialog = ({ market, option, isOpen, onClose, onConfirm, balance }) => {
    const [shares, setShares] = useState(10);
    const [orderType, setOrderType] = useState('market'); // 'market' or 'limit'
    const [limitPrice, setLimitPrice] = useState(option?.price || 0.5);

    if (!market || !option) return null;

    const price = orderType === 'market' ? option.price : limitPrice;
    // stake = number of shares (integer points deducted from balance)
    const stakePoints = Math.max(1, Math.min(100, Math.round(shares)));
    // Display cost in "$" for the user (shares × price)
    const cost = shares * price;
    const potentialPayout = shares * 1; // $1 per winning share
    const potentialProfit = potentialPayout - cost;

    const isYes = option.id === 'yes' || option.id === 'odd' || option.id === 'red' || option.id === 'favourite' || option.id === 'close';

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="text-xl">{market.icon}</span>
                        Buy {option.name} Shares
                    </DialogTitle>
                    <DialogDescription>
                        {market.title}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Current price */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-sm text-muted-foreground">Current Price</span>
                        <span className={`text-xl font-bold ${isYes ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatPrice(option.price)}
                        </span>
                    </div>

                    {/* Order type */}
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            variant={orderType === 'market' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setOrderType('market')}
                        >
                            Market Order
                        </Button>
                        <Button
                            variant={orderType === 'limit' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setOrderType('limit')}
                        >
                            Limit Order
                        </Button>
                    </div>

                    {orderType === 'limit' && (
                        <div className="space-y-2">
                            <Label>Limit Price</Label>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setLimitPrice(Math.max(0.01, limitPrice - 0.01))}
                                >
                                    <Minus className="h-4 w-4" />
                                </Button>
                                <Input
                                    type="number"
                                    value={limitPrice}
                                    onChange={(e) => setLimitPrice(parseFloat(e.target.value) || 0)}
                                    step="0.01"
                                    min="0.01"
                                    max="0.99"
                                    className="text-center font-data"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setLimitPrice(Math.min(0.99, limitPrice + 0.01))}
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Shares */}
                    <div className="space-y-2">
                        <Label>Number of Shares</Label>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShares(Math.max(1, shares - 10))}
                            >
                                <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                                type="number"
                                value={shares}
                                onChange={(e) => setShares(parseInt(e.target.value) || 1)}
                                min="1"
                                className="text-center font-data text-lg"
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShares(shares + 10)}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="flex gap-2">
                            {[10, 25, 50, 100].map((amt) => (
                                <Button
                                    key={amt}
                                    variant={shares === amt ? 'default' : 'outline'}
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setShares(amt)}
                                >
                                    {amt}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <Separator />

                    {/* Summary */}
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Cost</span>
                            <span className="font-data font-medium">{formatPrice(cost)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Potential Payout</span>
                            <span className="font-data font-medium text-emerald-600">{formatPrice(potentialPayout)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Potential Profit</span>
                            <span className={`font-data font-bold ${potentialProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {potentialProfit >= 0 ? '+' : ''}{formatPrice(potentialProfit)}
                            </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                            <span className="text-muted-foreground">Your Balance</span>
                            <span className="font-data">{(balance || 0).toLocaleString()} pts</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Points staked</span>
                            <span className="font-data font-medium">{stakePoints} pts</span>
                        </div>
                    </div>

                    {stakePoints > (balance || 0) && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>Insufficient balance ({balance || 0} pts)</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => onConfirm({ market, option, shares, price, cost, orderType })}
                        disabled={stakePoints > (balance || 0)}
                        className={isYes ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
                    >
                        Buy {shares} Shares
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ── Helper: seconds since timestamp ────────────────────────────────────────────
const secondsSince = (ts) => Math.floor((Date.now() - ts) / 1000);

const formatLastRefresh = (ts) => {
    const s = secondsSince(ts);
    if (s < 5)  return 'just now';
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
};

export default function PredictionsPage() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('markets');
    const [balance, setBalance] = useState(null);
    const [loadingBalance, setLoadingBalance] = useState(true);
    const [markets, setMarkets] = useState([]);
    const [loadingMarkets, setLoadingMarkets] = useState(true);
    const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [userPositions, setUserPositions] = useState({});
    const [myBets, setMyBets] = useState([]);
    const [loadingBets, setLoadingBets] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

    // Trade dialog state
    const [tradeDialog, setTradeDialog] = useState({ isOpen: false, market: null, option: null });

    // Live update state
    const [tick, setTick] = useState(0);
    const [priceChanges, setPriceChanges] = useState({});
    const [lastRefresh, setLastRefresh] = useState(Date.now());
    const marketsRef = useRef(markets);
    const priceChangeTimers = useRef({});

    // Keep ref updated
    useEffect(() => {
        marketsRef.current = markets;
    }, [markets]);

    // Derive live counts from current markets
    const liveCount = markets.filter(m => {
        const s = m.status === 'settled' ? 'settled' : m.isLive ? 'live' : getMarketStatus(m.race_time);
        return s === 'live' || s === 'starting';
    }).length;

    // True when any market is live or starting soon — drives fast polling
    const hasActiveRaces = liveCount > 0;

    // Fetch balance
    const fetchBalance = useCallback(async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/predictions/balance`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBalance(res.data.balance || 1000);
        } catch (err) {
            console.error('Failed to fetch balance:', err);
            setBalance(1000);
        } finally {
            setLoadingBalance(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch markets — background=true skips the full loading spinner
    const fetchMarkets = useCallback(async (background = false) => {
        if (!background) setLoadingMarkets(true);
        else setBackgroundRefreshing(true);

        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/predictions/live-markets`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const liveMarkets = res.data.markets || [];

            // Filter out markets settled more than 30 mins ago
            const activeMarkets = liveMarkets.filter(m => {
                if (!m.race_time) return true;
                const raceTime = new Date(m.race_time);
                const now = new Date();
                const diffMins = (raceTime - now) / (1000 * 60);
                return diffMins > -30;
            });

            const formatted = activeMarkets.map(m => ({
                ...m,
                options: m.options.map(opt => ({
                    ...opt,
                    price: oddsToPrice(opt.odds),
                })),
                totalVolume: m.volume || 0
            }));

            // ── Price-change animation tracking ────────────────────────────────
            const prev = marketsRef.current;
            if (prev.length > 0 && background) {
                const newChanges = {};
                formatted.forEach(newM => {
                    const oldM = prev.find(p => p.id === newM.id);
                    if (!oldM) return;
                    newM.options.forEach((newOpt, idx) => {
                        const oldOpt = oldM.options[idx];
                        if (!oldOpt) return;
                        const diff = newOpt.price - oldOpt.price;
                        if (Math.abs(diff) >= 0.01) {
                            const key = `${newM.id}_${newOpt.id}`;
                            newChanges[key] = diff > 0 ? 'up' : 'down';
                            newOpt.priceChange = diff;
                            // Clear flash after 3s
                            clearTimeout(priceChangeTimers.current[key]);
                            priceChangeTimers.current[key] = setTimeout(() => {
                                setPriceChanges(prev => {
                                    const next = { ...prev };
                                    delete next[key];
                                    return next;
                                });
                            }, 3000);
                        }
                    });
                });
                if (Object.keys(newChanges).length > 0) {
                    setPriceChanges(prev => ({ ...prev, ...newChanges }));
                }
            }

            // ── Settlement detection ────────────────────────────────────────────
            // If a market that was previously live/running is now settled,
            // schedule a follow-up refresh in 5s to capture the final result.
            const prevLiveIds = new Set(
                prev
                    .filter(m => {
                        const s = m.status === 'settled' ? 'settled' : m.isLive ? 'live' : getMarketStatus(m.race_time);
                        return s === 'live';
                    })
                    .map(m => m.id)
            );
            const justSettled = formatted.some(m => {
                const s = m.status === 'settled' ? 'settled' : m.isLive ? 'live' : getMarketStatus(m.race_time);
                return s === 'settled' && prevLiveIds.has(m.id);
            });
            if (justSettled) {
                setTimeout(() => fetchMarkets(true), 5000);
            }

            setMarkets(formatted.length > 0 ? formatted : []);
            setLastRefresh(Date.now());
        } catch (err) {
            console.error('Failed to fetch markets:', err);
            if (!background) setMarkets([]);
        } finally {
            setLoadingMarkets(false);
            setBackgroundRefreshing(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch my bets + build userPositions map
    const fetchMyBets = useCallback(async () => {
        setLoadingBets(true);
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/predictions/my-bets`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const bets = res.data.bets || [];
            setMyBets(bets);

            // Build positions map keyed by market id
            const posMap = {};
            bets.forEach(bet => {
                if (bet.status === 'pending' && bet.market_id) {
                    if (!posMap[bet.market_id]) {
                        posMap[bet.market_id] = {
                            side: bet.selection_name,
                            shares: 0,
                            avgPrice: 0,
                            totalCost: 0,
                        };
                    }
                    const pos = posMap[bet.market_id];
                    pos.shares += bet.stake || 0;
                    pos.totalCost += (bet.stake || 0) * (1 / (bet.odds || 2));
                    pos.avgPrice = pos.totalCost / pos.shares;
                }
            });
            setUserPositions(posMap);
        } catch (err) {
            console.error('Failed to fetch bets:', err);
        } finally {
            setLoadingBets(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch leaderboard
    const fetchLeaderboard = useCallback(async () => {
        setLoadingLeaderboard(true);
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/predictions/leaderboard`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLeaderboard(res.data.leaderboard || []);
        } catch (err) {
            console.error('Failed to fetch leaderboard:', err);
        } finally {
            setLoadingLeaderboard(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle trade
    const handleTrade = (market, option) => {
        setTradeDialog({ isOpen: true, market, option });
    };

    // Confirm trade
    const confirmTrade = async (trade) => {
        try {
            const token = getToken();

            // stake_per_bet must be an integer ≥ 1 (backend enforces 1–100)
            const stakePerBet = Math.max(1, Math.min(100, Math.round(trade.shares)));

            const payload = {
                predictions: [{
                    race_id: trade.market.race_id,
                    race_name: trade.market.race_name || trade.market.title || '',
                    meeting_name: trade.market.meeting_name || '',
                    race_time: trade.market.race_time || '',
                    market_type: trade.market.market_type,
                    market_name: trade.market.market_name || trade.market.title || '',
                    selection: trade.option.id,
                    selection_name: trade.option.name,
                    odds: parseFloat((1 / (trade.price || 0.5)).toFixed(4)),
                }],
                stake_per_bet: stakePerBet,
            };

            const res = await axios.post(
                `${API_URL}/api/predictions/place`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success(`Placed ${trade.shares} × ${trade.option.name}!`);
            setBalance(res.data.new_balance);
            fetchMarkets(true);
        } catch (err) {
            // Pydantic 422 returns detail as an array — normalise to a readable string
            const raw = err.response?.data?.detail;
            let msg = 'Trade failed';
            if (typeof raw === 'string') {
                msg = raw;
            } else if (Array.isArray(raw) && raw.length > 0) {
                msg = raw.map(e => e.msg || JSON.stringify(e)).join('; ');
            } else if (err.message) {
                msg = err.message;
            }
            console.error('Trade error', err.response?.status, err.response?.data);
            toast.error(msg);
        } finally {
            setTradeDialog({ isOpen: false, market: null, option: null });
        }
    };

    // Countdown timer — 1s tick drives "Xs ago" and countdown labels
    useEffect(() => {
        const interval = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Smart auto-refresh:
    //   • 10s when any market is live/starting soon
    //   • 30s otherwise
    useEffect(() => {
        const delay = hasActiveRaces ? 10_000 : 30_000;
        const id = setInterval(() => {
            if (activeTab === 'markets' && !loadingMarkets) {
                fetchMarkets(true);
            }
        }, delay);
        return () => clearInterval(id);
    }, [hasActiveRaces, activeTab, loadingMarkets, fetchMarkets]);

    // Initial load
    useEffect(() => {
        fetchBalance();
        fetchMarkets(false);
    }, [fetchBalance, fetchMarkets]);

    useEffect(() => {
        if (activeTab === 'positions') {
            fetchMyBets();
        } else if (activeTab === 'leaderboard') {
            fetchLeaderboard();
        }
    }, [activeTab, fetchMyBets, fetchLeaderboard]);

    // Filter markets
    const filteredMarkets = markets.filter(market => {
        if (selectedCategory === 'all') return true;
        if (selectedCategory === 'live') return getMarketStatus(market.race_time) === 'live' || market.isLive;
        if (selectedCategory === 'upcoming') return getMarketStatus(market.race_time) === 'upcoming';
        return market.market_type === selectedCategory;
    });

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="border-b bg-card/50 backdrop-blur-sm sticky top-16 z-40">
                <div className="container-app py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <h1 className="text-2xl font-heading font-bold page-title">Prediction Markets</h1>
                            <Badge variant="outline" className="gap-1">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                                Live
                            </Badge>
                        </div>

                        {/* Balance + refresh */}
                        <div className="flex items-center gap-3">
                            {/* Last refresh / refreshing indicator */}
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {backgroundRefreshing ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <span suppressHydrationWarning>
                                        Updated {formatLastRefresh(lastRefresh)}
                                    </span>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => fetchMarkets(true)}
                                disabled={backgroundRefreshing || loadingMarkets}
                                title="Refresh markets"
                            >
                                <RefreshCw className={`h-4 w-4 ${backgroundRefreshing ? 'animate-spin' : ''}`} />
                            </Button>
                            <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-lg">
                                <DollarSign className="h-4 w-4 text-emerald-500" />
                                {loadingBalance ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <span className="font-bold font-data text-lg">
                                        {formatPrice(balance || 0)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Category filters */}
                    <ScrollArea className="mt-4 -mx-4 px-4">
                        <div className="flex items-center gap-2 pb-2">
                            {MARKET_CATEGORIES.map(cat => (
                                <Button
                                    key={cat.id}
                                    variant={selectedCategory === cat.id ? "default" : "ghost"}
                                    size="sm"
                                    className="flex-shrink-0"
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name}
                                </Button>
                            ))}
                        </div>
                        <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                </div>
            </div>

            {/* Disclaimer */}
            <div className="container-app pt-4">
                <Alert className="border-primary/30 bg-primary/5">
                    <Info className="h-4 w-4 text-primary" />
                    <AlertDescription className="text-sm">
                        <strong>How it works:</strong> Buy shares at market price ($0.01 - $0.99).
                        Winning shares pay out <strong>$1.00</strong>. Price = probability (e.g., $0.60 = 60% chance).
                    </AlertDescription>
                </Alert>
            </div>

            {/* Main Content */}
            <div className="container-app py-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="markets" className="gap-2">
                            <Target className="h-4 w-4" />
                            Markets
                            {liveCount > 0 && (
                                <Badge variant="destructive" className="ml-1 h-4 min-w-4 px-1 text-[10px]">
                                    {liveCount}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="positions" className="gap-2">
                            <Wallet className="h-4 w-4" />
                            Positions
                        </TabsTrigger>
                        <TabsTrigger value="leaderboard" className="gap-2">
                            <Trophy className="h-4 w-4" />
                            Leaderboard
                        </TabsTrigger>
                    </TabsList>

                    {/* Markets Tab */}
                    <TabsContent value="markets">
                        {loadingMarkets ? (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(6)].map((_, i) => (
                                    <Card key={i} className="animate-pulse">
                                        <CardContent className="p-4">
                                            <div className="h-8 bg-muted rounded mb-4" />
                                            <div className="h-24 bg-muted rounded mb-3" />
                                            <div className="h-2 bg-muted rounded" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : filteredMarkets.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted/50 mb-6">
                                    <Lock className="h-10 w-10 text-muted-foreground/50" />
                                </div>
                                <h3 className="font-heading font-semibold text-xl mb-2">Prediction Markets Closed</h3>
                                <p className="text-muted-foreground max-w-md mx-auto">
                                    There are no prediction markets available today. Check back later when new races are enabled.
                                </p>
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredMarkets.map(market => (
                                    <MarketCard
                                        key={market.id}
                                        market={market}
                                        onTrade={handleTrade}
                                        userPositions={userPositions}
                                        priceChanges={priceChanges}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Positions Tab */}
                    <TabsContent value="positions">
                        <Card>
                            <CardContent className="p-6">
                                {loadingBets ? (
                                    <div className="py-8 text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                                    </div>
                                ) : myBets.length === 0 && Object.keys(userPositions).length === 0 ? (
                                    <div className="py-12 text-center text-muted-foreground">
                                        <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No positions yet. Start trading!</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* API positions */}
                                        {myBets.map((bet) => (
                                            <div
                                                key={bet.id}
                                                className={`p-4 rounded-lg border ${
                                                    bet.status === 'won' ? 'bg-emerald-500/10 border-emerald-500/30' :
                                                    bet.status === 'lost' ? 'bg-rose-500/10 border-rose-500/30' :
                                                    'bg-muted/50'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-medium">{bet.market_name}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {bet.selection_name} @ {formatPrice(1/bet.odds)}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <Badge variant={
                                                            bet.status === 'won' ? 'default' :
                                                            bet.status === 'lost' ? 'destructive' :
                                                            'secondary'
                                                        }>
                                                            {bet.status.toUpperCase()}
                                                        </Badge>
                                                        {bet.status === 'won' && (
                                                            <p className="text-sm font-bold text-emerald-600 mt-1">
                                                                +{formatPrice(bet.payout)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Leaderboard Tab */}
                    <TabsContent value="leaderboard">
                        <Card>
                            <CardContent className="p-6">
                                {loadingLeaderboard ? (
                                    <div className="py-8 text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                                    </div>
                                ) : leaderboard.length === 0 ? (
                                    <div className="py-12 text-center text-muted-foreground">
                                        <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No traders yet. Be the first!</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-3 px-2">#</th>
                                                    <th className="text-left py-3 px-2">Trader</th>
                                                    <th className="text-right py-3 px-2">Trades</th>
                                                    <th className="text-right py-3 px-2">Win Rate</th>
                                                    <th className="text-right py-3 px-2">P&L</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leaderboard.map((entry) => (
                                                    <tr
                                                        key={entry.user_id}
                                                        className={`border-b hover:bg-muted/50 ${
                                                            entry.user_id === user?.id ? 'bg-primary/5' : ''
                                                        }`}
                                                    >
                                                        <td className="py-3 px-2">
                                                            {entry.rank <= 3 ? (
                                                                <span className="text-lg">
                                                                    {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                                                                </span>
                                                            ) : entry.rank}
                                                        </td>
                                                        <td className="py-3 px-2 font-medium">
                                                            {entry.user_name}
                                                            {entry.user_id === user?.id && (
                                                                <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-2 text-right font-data">{entry.total_bets}</td>
                                                        <td className="py-3 px-2 text-right font-data">{entry.win_rate?.toFixed(1)}%</td>
                                                        <td className={`py-3 px-2 text-right font-data font-bold ${
                                                            entry.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                                        }`}>
                                                            {entry.profit >= 0 ? '+' : ''}{formatPrice(entry.profit || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Trade Dialog */}
            <TradeDialog
                market={tradeDialog.market}
                option={tradeDialog.option}
                isOpen={tradeDialog.isOpen}
                onClose={() => setTradeDialog({ isOpen: false, market: null, option: null })}
                onConfirm={confirmTrade}
                balance={balance}
            />
        </div>
    );
}
